import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, Search } from 'lucide-react'
import { DateTime } from 'luxon'
import { Button } from './ui/Button'
import { ALL_TIMEZONES, formatUtcOffset } from '../lib/timezones'
import { api, extractApiError } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

/**
 * Shared company-timezone controls. ONE source of truth for the picker,
 * the save flow, and the first-login prompt — used by the Report Schedule
 * page, My Preferences (Region & formats), and the layout-level prompt so the three can
 * never drift.
 */

const INPUT_BASE =
  'w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-accent)] focus:ring-2 focus:ring-[color:var(--color-accent)]/20 disabled:opacity-60 disabled:cursor-not-allowed'

/**
 * Company-timezone dropdown rendered as a compact TABLE — a native
 * <select> can't align columns, so this is a button + popover listbox
 * with a header row (Time zone | UTC offset) and one row per zone (zone
 * name on top, IANA id beneath, offset right-aligned). Closes on
 * outside-click / Escape. `options` are `{ value, name, offset }`; an
 * empty `value` is the "Not set" row (its wording: `emptyLabel`). `id`,
 * `ariaLabel` and `disabled` are optional (defaults unchanged).
 *
 * The list is rendered through a PORTAL to <body> with fixed positioning
 * anchored to the trigger, so it floats ABOVE any overflow-clipping
 * ancestor (e.g. the first-login modal) instead of being clipped inside
 * it. Position is recomputed on open, scroll, and resize.
 */
export function TimezoneSelect({
  value,
  onChange,
  options,
  emptyLabel = 'Not set — use server default',
  // Optional: the trigger's id (so a <label htmlFor> can point at it), its
  // accessible name, and a disabled state. Defaults keep existing callers as-is.
  id,
  ariaLabel = 'Company timezone',
  disabled = false,
}) {
  const [open, setOpen] = useState(false)
  const [rect, setRect] = useState(null)
  const [query, setQuery] = useState('')
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const searchRef = useRef(null)

  const reposition = useCallback(() => {
    const el = triggerRef.current
    if (el) setRect(el.getBoundingClientRect())
  }, [])

  // Type-to-search across the ~400 IANA zones: match the prettified name
  // (e.g. "Europe/Paris"), the raw IANA id, or the UTC-offset label (so
  // "+5:30" jumps to the half-hour zones). Empty query shows everything.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    const numeric = q.replace(/^utc/, '').replace(/^\+/, '')
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.offset && o.offset.toLowerCase().includes(q)) ||
        (o.offset && o.offset.toLowerCase().includes(numeric)),
    )
  }, [options, query])

  // Focus the search box when the panel opens (a tick after the portal
  // mounts), so the user can type immediately — the core of the
  // search-first picker.
  useEffect(() => {
    if (!open) return undefined
    const t = setTimeout(() => searchRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    reposition()
    function onDocPointer(e) {
      const t = e.target
      if (
        !triggerRef.current?.contains(t) &&
        !panelRef.current?.contains(t)
      ) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        // Stop the press here so closing the dropdown doesn't also close
        // a surrounding modal on the same Escape.
        e.stopPropagation()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocPointer)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      document.removeEventListener('mousedown', onDocPointer)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition])

  const selected = options.find((o) => o.value === value)
  const triggerLabel = value
    ? (selected?.name ?? value)
    : emptyLabel
  const triggerOffset = selected?.offset ?? ''

  function pick(next) {
    onChange(next)
    setOpen(false)
    setQuery('')
  }

  const ROW_BASE =
    'grid w-full grid-cols-[1fr_auto] items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-[color:var(--color-card-hover)] cursor-pointer'

  const PANEL_W = rect ? Math.max(rect.width, 352) : 352
  let panelStyle = null
  if (rect) {
    const GAP = 4
    const MARGIN = 8
    const spaceBelow = window.innerHeight - rect.bottom - MARGIN
    const spaceAbove = rect.top - MARGIN
    // Flip the list upward when there isn't enough room below the trigger
    // (e.g. the card sits near the bottom of the page), and cap its height
    // to the available space so it can never be clipped by the viewport.
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow
    panelStyle = {
      position: 'fixed',
      left: Math.max(
        MARGIN,
        Math.min(rect.left, window.innerWidth - PANEL_W - MARGIN),
      ),
      width: PANEL_W,
      maxHeight: Math.max(140, Math.min(288, openUp ? spaceAbove : spaceBelow)),
      zIndex: 1000,
    }
    if (openUp) {
      panelStyle.bottom = window.innerHeight - rect.top + GAP
    } else {
      panelStyle.top = rect.bottom + GAP
    }
  }

  const panel =
    open && rect
      ? createPortal(
          <div
            ref={panelRef}
            role="listbox"
            style={panelStyle}
            className="overflow-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg"
          >
            <div className="sticky top-0 border-b border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-2">
              <div className="relative">
                <Search
                  size={14}
                  aria-hidden="true"
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter selects the first match — the fast path for
                    // "type my city, hit Enter".
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      if (filtered.length > 0) pick(filtered[0].value)
                    }
                  }}
                  placeholder="Search city, region or UTC offset…"
                  aria-label="Search time zones"
                  className="h-9 w-full rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] pl-8 pr-3 text-sm text-[color:var(--color-ink)] outline-none placeholder:text-[color:var(--color-ink-subtle)] focus:border-[color:var(--color-accent)]"
                />
              </div>
            </div>

            {/* "Not set" reset — hidden while searching (it can't match a
             *  query and would just be visual noise), same as the country
             *  picker's "All countries" row. */}
            {!query.trim() ? (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                onClick={() => pick('')}
                className={ROW_BASE + (!value ? ' bg-[color:var(--color-accent-bg)]' : '')}
              >
                <span className="text-sm text-[color:var(--color-ink)]">
                  {emptyLabel}
                </span>
                <span />
              </button>
            ) : null}

            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-[color:var(--color-ink-muted)]">
                No time zones found.
              </div>
            ) : (
              filtered.map((o) => {
                const isSel = o.value === value
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    onClick={() => pick(o.value)}
                    className={
                      ROW_BASE + (isSel ? ' bg-[color:var(--color-accent-bg)]' : '')
                    }
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-[color:var(--color-ink-strong)]">
                        {o.name}
                      </span>
                      <span className="block truncate text-xs text-[color:var(--color-ink-muted)]">
                        {o.value}
                      </span>
                    </span>
                    <span className="self-center text-xs tabular-nums text-[color:var(--color-ink-muted)]">
                      {o.offset ?? ''}
                    </span>
                  </button>
                )
              })
            )}
          </div>,
          document.body,
        )
      : null

  return (
    <div className="relative w-full max-w-md">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!open) setQuery('')
          setOpen((o) => !o)
        }}
        className={INPUT_BASE + ' flex items-center justify-between gap-2 text-left'}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <span className="flex items-center gap-2 shrink-0">
          {triggerOffset ? (
            <span className="text-xs tabular-nums text-[color:var(--color-ink-muted)]">
              {triggerOffset}
            </span>
          ) : null}
          <ChevronDown size={16} className="opacity-60" />
        </span>
      </button>
      {panel}
    </div>
  )
}

/**
 * Shared state + save flow for the company timezone. Reads the current
 * zone from the auth store, saves via the `company.manage`-gated PATCH
 * /admin/company endpoint, then refreshes the session so every date
 * display + the reminder runner pick up the new zone. `onSaved` fires
 * after a successful save. Used by both the company editor in My Preferences and the
 * first-login prompt so the two never drift.
 */
function useCompanyTimezone(onSaved, suggestedTimezone) {
  const tenantTimezone = useAuthStore((s) => s.tenant?.timezone) || null
  const [tzDraft, setTzDraft] = useState(tenantTimezone ?? '')

  // Re-sync when the store's tenant timezone changes (e.g. right after a
  // successful save calls refreshUser()).
  useEffect(() => {
    setTzDraft(tenantTimezone ?? '')
  }, [tenantTimezone])

  // The Company country card suggested a matching zone → pre-fill the draft so
  // the admin can review it and click Save. NOT saved automatically, so a
  // deliberately-set timezone is only replaced once the admin confirms.
  useEffect(() => {
    if (suggestedTimezone) setTzDraft(suggestedTimezone)
  }, [suggestedTimezone])

  // Offer the full IANA catalogue (searchable). A zone the catalogue doesn't
  // list — the saved one, or the draft a country suggestion filled in — is
  // kept as a leading option so the dropdown shows the real value rather than
  // silently misrepresenting (or overwriting) it on the next save. Browsers
  // list some zones under an older alias (Chromium has "Asia/Calcutta", the
  // country suggestion uses "Asia/Kolkata"); a VALID zone like that shows
  // with its name and UTC offset like any other. Only an unknown value is
  // marked "(current)".
  const options = useMemo(() => {
    const list = [...ALL_TIMEZONES]
    for (const zone of [tenantTimezone, tzDraft]) {
      if (!zone || list.some((z) => z.value === zone)) continue
      const dt = DateTime.now().setZone(zone)
      list.unshift(
        dt.isValid
          ? { value: zone, name: zone.replace(/_/g, ' '), offset: formatUtcOffset(dt.offset) }
          : { value: zone, name: `${zone} (current)`, offset: '' },
      )
    }
    return list
  }, [tenantTimezone, tzDraft])

  const mutation = useMutation({
    mutationFn: async (tz) => {
      const res = await api.patch('/admin/company', { timezone: tz || null })
      return res.data
    },
    onSuccess: async () => {
      toast.success('Company timezone saved successfully.')
      await useAuthStore.getState().refreshUser()
      onSaved?.()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const unchanged = (tzDraft || '') === (tenantTimezone || '')

  return { tenantTimezone, tzDraft, setTzDraft, options, mutation, unchanged }
}

/**
 * Picker + Save for the company timezone — used in My Preferences → Region & formats.
 */
export function CompanyTimezoneEditor({ onSaved, suggestedTimezone, onTimezoneChange }) {
  const { tenantTimezone, tzDraft, setTzDraft, options, mutation, unchanged } =
    useCompanyTimezone(onSaved, suggestedTimezone)

  return (
    <div className="flex flex-wrap items-center gap-3">
      <TimezoneSelect
        value={tzDraft}
        onChange={(tz) => {
          setTzDraft(tz)
          // Suggest a matching country in the neighbouring card (the admin
          // still Saves it there — nothing auto-saves here).
          onTimezoneChange?.(tz)
        }}
        options={options}
      />
      <Button
        onClick={() => mutation.mutate(tzDraft)}
        loading={mutation.isPending}
        disabled={mutation.isPending || unchanged}
      >
        Save Timezone
      </Button>
      {tenantTimezone ? (
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          Current:{' '}
          <span className="font-medium text-[color:var(--color-ink-strong)]">
            {tenantTimezone}
          </span>
        </span>
      ) : (
        <span className="text-xs text-[color:var(--color-warning)]">
          Not set — reminders use the server clock.
        </span>
      )}
    </div>
  )
}

/**
 * First-login timezone setup. Instead of prompting, this AUTO-DETECTS the
 * Company Admin's browser timezone and saves it as the company timezone
 * silently — no dialog, no nag. Runs once when an admin's tenant has no
 * zone yet; best-effort (on failure it leaves the zone unset and falls
 * back to the server clock). The admin can change it any time via the
 * `CompanyTimezoneEditor` in My Preferences → Region & formats. Renders nothing.
 */
export function CompanyTimezonePrompt() {
  const status = useAuthStore((s) => s.status)
  const role = useAuthStore((s) => s.user?.role)
  const tenantTimezone = useAuthStore((s) => s.tenant?.timezone) || null
  const attemptedRef = useRef(false)

  useEffect(() => {
    const needsDetect =
      status === 'authenticated' &&
      role === 'company_admin' &&
      !tenantTimezone
    if (!needsDetect || attemptedRef.current) return
    attemptedRef.current = true
    const detected = DateTime.local().zoneName
    if (!detected) return
    void (async () => {
      try {
        await api.patch('/admin/company', { timezone: detected })
        await useAuthStore.getState().refreshUser()
      } catch {
        // Best-effort — don't interrupt the user; the zone stays unset
        // (server-clock fallback) and is editable in My Preferences → Region & formats.
      }
    })()
  }, [status, role, tenantTimezone])

  return null
}

